/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

/**
 * NotFoundPage Component
 * 
 * A 404 error page displayed when a user navigates to a route that doesn't exist.
 * This component provides a user-friendly error message with navigation options
 * to help users recover from the error situation.
 * 
 * Key features:
 * - Clear 404 error message with explanatory text
 * - "Go to Dashboard" button for returning to the application's main page
 * - "Go Back" button that uses browser history to return to the previous page
 * - Maintains consistent branding and styling with the rest of the application
 * - Responsive design that works well on all screen sizes
 * - Complete page layout with header and footer
 * 
 * This component is used by the router when no matching route is found, or can be
 * rendered explicitly for resources that don't exist.
 * 
 * @component
 */

import React from 'react';
import { Link } from 'react-router-dom';

/**
 * 404 Not Found page component
 * 
 * @returns {React.ReactElement} The rendered NotFoundPage component
 */
const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-800">
        <div className="container mx-auto">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#A855F6] to-[#E33786]">
              BrandHealth 360
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6 text-center">
        <div className="max-w-lg">
          <div className="mb-6">
            <span className="text-8xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#A855F6] to-[#E33786]">
              404
            </span>
          </div>
          
          <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
          
          <p className="text-[#9DB3B9] mb-8">
            Oops! The page you're looking for doesn't exist or has been moved.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-6 py-3 bg-[#5511A3] text-white rounded-lg hover:bg-[#A855F6] transition flex items-center justify-center"
            >
              <i className="fa-solid fa-home mr-2"></i>
              Go to Dashboard
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-transparent border border-[#5511A3] text-[#D977DD] rounded-lg hover:border-[#A855F6] hover:text-[#A855F6] transition flex items-center justify-center"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Go Back
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 px-6 border-t border-gray-800 text-[#9DB3B9]">
        <div className="container mx-auto flex justify-between items-center">
          <p className="text-sm">© 2025 Food Trends Engine. All rights reserved.</p>
          <div className="flex items-center space-x-4 text-sm">
            <span className="hover:text-white transition cursor-pointer">Privacy Policy</span>
            <span className="hover:text-white transition cursor-pointer">Terms of Service</span>
            <span className="hover:text-white transition cursor-pointer">Contact Support</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

/**
 * Export the NotFoundPage component as the default export
 * This allows importing the component using:
 * import NotFoundPage from './pages/NotFoundPage';
 */
export default NotFoundPage;